import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Drawer, Row, Space, Spin, Typography } from 'antd';
import TyreSchematicView from '@/components/pmv/TyreSchematicView';
import BatterySchematicView from '@/components/pmv/BatterySchematicView';
import PmvInstallToPositionModal from '@/components/pmv/PmvInstallToPositionModal';
import pmvService, { type PmvTyreSchematicCard } from '@/services/pmv.service';
import type { BatterySchematicCard } from '@/components/pmv/BatterySchematicView';

const { Text, Title } = Typography;

type Readiness = {
  tyres?: { label: string; fitted: number; slots: number; attention: number };
  batteries?: { label: string; fitted: number; slots: number; attention: number };
  policy_note?: string;
};

/**
 * Equipment Passport — Components tab body (W3.14).
 * Embeds operational schematics; does not auto-block deployment.
 */
const PmvEquipmentComponentsPanel: React.FC<{
  profileId: string;
  readiness?: Readiness | null;
}> = ({ profileId, readiness }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedTyre, setSelectedTyre] = useState<PmvTyreSchematicCard | null>(null);
  const [selectedBat, setSelectedBat] = useState<BatterySchematicCard | null>(null);
  const [installTyre, setInstallTyre] = useState<PmvTyreSchematicCard | null>(null);
  const [installBat, setInstallBat] = useState<BatterySchematicCard | null>(null);

  const tyreQ = useQuery({
    queryKey: ['pmv-tyre-schematic', profileId],
    queryFn: () => pmvService.getTyreSchematic(profileId),
    enabled: Boolean(profileId),
    retry: false,
  });
  const batQ = useQuery({
    queryKey: ['pmv-battery-schematic', profileId],
    queryFn: () => pmvService.getBatterySchematic(profileId),
    enabled: Boolean(profileId),
    retry: false,
  });

  const refreshLayouts = () => {
    void qc.invalidateQueries({ queryKey: ['pmv-tyre-schematic', profileId] });
    void qc.invalidateQueries({ queryKey: ['pmv-battery-schematic', profileId] });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Components are lifecycle evidence on this unit"
        description={
          readiness?.policy_note ||
          'Attention on tyres/batteries does not automatically block deployment unless readiness policy says so.'
        }
      />

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card size="small" title="Tyres">
            <Text>{readiness?.tyres?.label || '—'}</Text>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Batteries">
            <Text>{readiness?.batteries?.label || '—'}</Text>
          </Card>
        </Col>
      </Row>

      <Card
        title="Tyre schematic"
        size="small"
        extra={
          <Button type="link" onClick={() => navigate(`/asset/pmv/${profileId}/tyres`)}>
            Full tyre workspace
          </Button>
        }
      >
        {tyreQ.isLoading ? <Spin /> : null}
        {tyreQ.isError ? (
          <Alert type="info" showIcon message="No tyre layout applied yet" />
        ) : (
          <TyreSchematicView
            data={tyreQ.data}
            pmvProfileId={profileId}
            allowVacantSelect
            onLayoutApplied={refreshLayouts}
            onSelectCard={(c) => {
              if (c.status === 'VACANT') setInstallTyre(c);
              else setSelectedTyre(c);
            }}
          />
        )}
      </Card>

      <Card
        title="Battery bank"
        size="small"
        extra={
          <Button type="link" onClick={() => navigate(`/asset/pmv/${profileId}/batteries`)}>
            Full battery workspace
          </Button>
        }
      >
        {batQ.isLoading ? <Spin /> : null}
        {batQ.isError ? (
          <Alert type="info" showIcon message="No battery layout applied yet" />
        ) : (
          <BatterySchematicView
            data={batQ.data as Parameters<typeof BatterySchematicView>[0]['data']}
            pmvProfileId={profileId}
            allowVacantSelect
            onLayoutApplied={refreshLayouts}
            onSelectCard={(c) => {
              if (c.status === 'VACANT') setInstallBat(c);
              else setSelectedBat(c);
            }}
          />
        )}
      </Card>

      <PmvInstallToPositionModal
        open={Boolean(installTyre)}
        profileId={profileId}
        family="TYRE"
        position={
          installTyre
            ? {
                position_id: installTyre.position_id,
                position_code: installTyre.position_code,
                display_label: installTyre.display_label,
                family: 'TYRE',
              }
            : null
        }
        onClose={() => setInstallTyre(null)}
        onInstalled={refreshLayouts}
      />
      <PmvInstallToPositionModal
        open={Boolean(installBat)}
        profileId={profileId}
        family="BATTERY"
        position={
          installBat
            ? {
                position_id: installBat.position_id,
                position_code: installBat.position_code,
                display_label: installBat.display_label,
                family: 'BATTERY',
              }
            : null
        }
        onClose={() => setInstallBat(null)}
        onInstalled={refreshLayouts}
      />

      <Drawer
        open={Boolean(selectedTyre)}
        onClose={() => setSelectedTyre(null)}
        title={selectedTyre?.component_number || 'Tyre'}
        width={360}
      >
        {selectedTyre ? (
          <Space direction="vertical">
            <Text>
              {selectedTyre.display_label} · {selectedTyre.condition_code || '—'}
            </Text>
            <Text type="secondary">
              Tread {selectedTyre.tread_depth_mm != null ? `${selectedTyre.tread_depth_mm} mm` : '—'}
            </Text>
            <Button
              type="primary"
              disabled={!selectedTyre.component_id}
              onClick={() => navigate(`/asset/pmv/components/${selectedTyre.component_id}`)}
            >
              Open Component Passport
            </Button>
            <Button onClick={() => navigate(`/asset/pmv/${profileId}/tyres`)}>Inspect / rotate</Button>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(selectedBat)}
        onClose={() => setSelectedBat(null)}
        title={selectedBat?.component_number || 'Battery'}
        width={360}
      >
        {selectedBat ? (
          <Space direction="vertical">
            <Title level={5} style={{ margin: 0 }}>
              {selectedBat.display_label}
            </Title>
            <Text>{selectedBat.condition_code || '—'}</Text>
            <Text type="secondary">
              SOH {selectedBat.latest_soh_pct != null ? `${selectedBat.latest_soh_pct}%` : '—'}
            </Text>
            <Button
              type="primary"
              disabled={!selectedBat.component_id}
              onClick={() => navigate(`/asset/pmv/components/${selectedBat.component_id}`)}
            >
              Open Component Passport
            </Button>
            <Button onClick={() => navigate(`/asset/pmv/${profileId}/batteries`)}>
              Inspect / replace
            </Button>
          </Space>
        ) : null}
      </Drawer>
    </Space>
  );
};

export default PmvEquipmentComponentsPanel;
